// @ts-check
import React from 'react';
import _ from 'lodash';
import Icon from 'shared/components/Icon';
import { WatchFieldValue, Field } from 'utils/reduxFormWrapper';
import styles from './Conditional.module.scss';
import {
  SelectInput,
  PlainSelectInput,
  CountySelectionInput,
} from 'shared/containers/portal_layout/HForm';
import { PayerGroupSelectInput } from 'shared/components/PayerGroupSelect';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { Conditional as ConditionalType, RulableField as RulableFieldType } from '../types'; // eslint-disable-line no-unused-vars

/** @typedef {{
  attributes: ConditionalType,
  rulableFields: RulableFieldType[],
}} ViewConditionalPropsType */

/** @type { React.SFC<ViewConditionalPropsType> } */
const ViewConditional = ({ attributes, rulableFields }) => {
  if (!attributes.rulableFieldIdentifier) {
    return <span>Invalid or Incomplete Condition (please edit)</span>;
  }

  const selectedRulableField = rulableFields.find(
    rf => rf.identifier === attributes.rulableFieldIdentifier
  );

  if (!selectedRulableField) {
    return <span>Invalid or Incomplete Condition (please edit)</span>;
  }

  const operator = selectedRulableField.operators.find(op => op.value === attributes.operator);
  if (!operator) {
    return <span>Invalid or Incomplete Condition (please edit)</span>;
  }

  let compareValueLabel;
  if (selectedRulableField.possibleValues && selectedRulableField.possibleValues.length > 0) {
    const matchingValue = selectedRulableField.possibleValues.find(
      possible => possible.value === attributes.compareValue
    );
    compareValueLabel = matchingValue ? matchingValue.text : '[Unknown (please edit)]';
  } else {
    compareValueLabel = attributes.compareValue;
  }
  return (
    <span>
      <span className={styles.field_display_name}>{selectedRulableField.longDisplayName}</span>{' '}
      {operator.label} <span className={styles.compare_value}>"{compareValueLabel}"</span>
    </span>
  );
};

const TrashButton = ({ onClick }) => (
  <div className={styles.trash_button} onClick={onClick} data-spec-id="remove-conditional">
    <Icon name="bin" />
  </div>
);

/**
 * Checks a list of options for the presence of a given value.
 * NB: A blank string "" is a valid option (no selection).
 *
 * @param {string} value
 * @param {{ text: string, value: string }[]} options
 * @returns {boolean}
 */
function valueIsValidOption(value, options) {
  return !value || options.map(o => o.value).includes(value);
}

/**
 * Build an "Unknown" AnswerOption
 *
 * @param {string} value
 * @returns {{ text: string, value: string }}
 */
function unknownOptionForValue(value) {
  return { text: `Unknown value (${value})`, value };
}

/** @typedef {{
  name: string,
  rulableField: RulableFieldType
}} CompareValuePropsType */

/** @type { React.SFC<CompareValuePropsType> } */
const CompareValue = ({ name, rulableField: { componentType, possibleValues } }) => {
  return (
    <WatchFieldValue
      name={`${name}.compareValue`}
      render={({ value: currentValue }) => {
        switch (componentType) {
          case 'textbox':
            return (
              <Field
                name={`${name}.compareValue`}
                component="input"
                type="text"
                data-spec-id="conditional-compare-value-input"
              />
            );
          case 'plainselect':
            return (
              <PlainSelectInput
                name={`${name}.compareValue`}
                options={
                  valueIsValidOption(currentValue, possibleValues)
                    ? possibleValues
                    : [unknownOptionForValue(currentValue), ...possibleValues]
                }
                labelKey="text"
                data-spec-id="conditional-compare-value-select"
              />
            );
          case 'select':
            return (
              <SelectInput
                name={`${name}.compareValue`}
                options={
                  valueIsValidOption(currentValue, possibleValues)
                    ? possibleValues
                    : [unknownOptionForValue(currentValue), ...possibleValues]
                }
                labelKey="text"
                data-spec-id="conditional-compare-value-select"
              />
            );
          case 'payerselect':
            // TODO: Beter handling if existing  payer value is no longer in list
            return (
              <PayerGroupSelectInput
                name={`${name}.compareValue`}
                options={possibleValues}
                data-spec-id="conditional-compare-value-select"
                className="input-large"
              />
            );
          case 'county':
            // TODO: Beter handling if existing county value is no longer in list
            return <CountySelectionInput name={`${name}.compareValue`} className="input-large" />;
          default:
            return null;
        }
      }}
    />
  );
};

const Conditional = connect()(({ dispatch, meta, name, remove, removable, rulableFields }) => {
  const groupedRulableFields = _.groupBy(rulableFields, rf => rf.groupName);
  const rulableFieldOptions = _.keys(groupedRulableFields).map(key => ({
    displayName: key,
    options: groupedRulableFields[key],
  }));

  const alsoChangeLongDisplayName = rulableFieldIdentifier => {
    const selectedRulableField = rulableFields.find(rf => rf.identifier === rulableFieldIdentifier);
    const longDisplayName = selectedRulableField ? selectedRulableField.longDisplayName : '';
    dispatch(change(meta.form, `${name}.longDisplayName`, longDisplayName));
  };

  return (
    <WatchFieldValue
      name={name}
      render={({ value: conditional }) => {
        const selectedRulableField = rulableFields.find(
          rf => rf.identifier === conditional.rulableFieldIdentifier
        );
        let finalRulableFieldOptions = rulableFieldOptions;
        if (conditional.rulableFieldIdentifier && !selectedRulableField) {
          // Add the "Unknown" option, if the existing value does not match any current rulable fields
          finalRulableFieldOptions = [
            { displayName: 'Unknown field', identifier: conditional.rulableFieldIdentifier },
          ].concat(finalRulableFieldOptions);
        }
        return (
          <div className={styles.conditional} data-spec-id="conditional">
            <PlainSelectInput
              name={`${name}.rulableFieldIdentifier`}
              options={finalRulableFieldOptions}
              prompt="Select..."
              labelKey="displayName"
              valueKey="identifier"
              data-spec-id="conditional-rulable-field-identifier"
              onChange={alsoChangeLongDisplayName}
            />
            {selectedRulableField ? (
              <div className={styles.conditional_fields}>
                <PlainSelectInput
                  name={`${name}.operator`}
                  options={selectedRulableField.operators}
                  prompt={false}
                  data-spec-id="conditional-operator"
                />
                <div className={styles.compare_value_input_container}>
                  <CompareValue name={name} rulableField={selectedRulableField} />
                </div>
              </div>
            ) : (
              conditional.rulableFieldIdentifier && (
                <span>
                  &nbsp;{conditional.longDisplayName} {conditional.operator}{' '}
                  {conditional.compareValue}
                </span>
              )
            )}
            {removable ? <TrashButton onClick={remove} /> : null}
          </div>
        );
      }}
    />
  );
});

export { Conditional, ViewConditional };
